import { useState, type ReactNode } from 'react';
import {
  ArrowClockwise,
  Article,
  CalendarX,
  ChartLine,
  Copy,
  DotsThreeVertical,
  Hash,
  MagnifyingGlass,
  Megaphone,
  PencilSimple,
  Plus,
  Trash,
  WarningCircle,
  XCircle,
} from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';
import { ConfirmationModal } from '@/components/core';
import { Button } from '@/components/ui-kit/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui-kit/dropdown-menu';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui-kit/table';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui-kit/tooltip';
import { useToast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';
import {
  useCancelCampaignScheduleRest,
  useDeleteCampaignRest,
  useDuplicateCampaignRest,
} from '../../hooks/use-campaigns-rest';
import type { Campaign } from '../../types/campaigns.types';
import { buildCloneCampaignName } from '../../utils/campaigns-mapper.utils';
import { canEditCampaign } from '../../utils/can-edit-campaign.utils';
import { WIZARD_STEP_VERSION } from '../../utils/wizard-step.utils';
import { CampaignStatusBadge } from '../campaign-status-badge/campaign-status-badge';

type CampaignsTableProps = {
  campaigns: Campaign[];
  isEmpty: boolean;
  hasActiveFilters: boolean;
  isLoading?: boolean;
  isError?: boolean;
  errorMessage?: string;
  onRetry?: () => void;
  onClearFilters?: () => void;
  onCreate?: () => void;
};

const SKELETON_ROW_COUNT = 6;
const formatCount = (value: number | null) => (value === null ? '—' : value.toLocaleString());
/** Rates are fractions; `null` (never sent, or nothing delivered) reads as a dash, not 0%. */
const formatRate = (value: number | null | undefined) =>
  typeof value === 'number' ? `${(value * 100).toFixed(1)}%` : '—';

const Sk = ({ className }: { className?: string }) => (
  <span className={cn('inline-block animate-pulse rounded bg-[#EDEEF0]', className)} />
);

const CampaignsTableSkeletonRows = () => (
  <>
    {Array.from({ length: SKELETON_ROW_COUNT }, (_, index) => (
      <TableRow key={`campaign-sk-${index}`} className="hover:bg-transparent">
        <TableCell className="px-4 py-3">
          <Sk className="block h-3.5 w-40" />
        </TableCell>
        <TableCell className="px-4 py-3">
          <Sk className="block h-3.5 w-[60px]" />
        </TableCell>
        <TableCell className="px-4 py-3">
          <Sk className="block h-5 w-20 rounded-full" />
        </TableCell>
        <TableCell className="px-4 py-3">
          <Sk className="ml-auto block h-3.5 w-[50px]" />
        </TableCell>
        <TableCell className="px-4 py-3">
          <Sk className="ml-auto block h-3.5 w-[50px]" />
        </TableCell>
        <TableCell className="px-4 py-3">
          <Sk className="ml-auto block h-3.5 w-[40px]" />
        </TableCell>
        <TableCell className="px-4 py-3">
          <Sk className="ml-auto block h-3.5 w-[40px]" />
        </TableCell>
        <TableCell className="px-4 py-3">
          <Sk className="block h-3.5 w-[110px]" />
        </TableCell>
        <TableCell className="px-4 py-3" />
      </TableRow>
    ))}
  </>
);

type DisabledMenuItemProps = {
  icon: ReactNode;
  label: string;
  tooltip: string;
};

const DisabledMenuItem = ({ icon, label, tooltip }: DisabledMenuItemProps) => (
  <Tooltip>
    <TooltipTrigger asChild>
      <span className="block w-full">
        <DropdownMenuItem
          disabled
          className="opacity-40"
          onSelect={(event) => event.preventDefault()}
        >
          {icon}
          {label}
        </DropdownMenuItem>
      </span>
    </TooltipTrigger>
    <TooltipContent side="left" className="max-w-[220px]">
      {tooltip}
    </TooltipContent>
  </Tooltip>
);

export const CampaignsTable = ({
  campaigns,
  isEmpty,
  hasActiveFilters,
  isLoading = false,
  isError = false,
  errorMessage,
  onRetry,
  onClearFilters,
  onCreate,
}: CampaignsTableProps) => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { mutateAsync: duplicateCampaign, isPending: isDuplicating } = useDuplicateCampaignRest();
  const { mutateAsync: deleteCampaign, isPending: isDeleting } = useDeleteCampaignRest();
  const { mutateAsync: cancelSchedule, isPending: isCancelling } = useCancelCampaignScheduleRest();
  const [duplicatingCampaignId, setDuplicatingCampaignId] = useState<string | null>(null);
  const [unscheduleTarget, setUnscheduleTarget] = useState<Campaign | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Campaign | null>(null);

  const handleDuplicateCampaign = async (campaign: Campaign) => {
    if (isDuplicating) return;

    setDuplicatingCampaignId(campaign.id);
    const cloneName = buildCloneCampaignName(campaign.name);

    try {
      await duplicateCampaign({ campaignId: campaign.id, input: { name: cloneName } });
      toast({
        variant: 'success',
        title: 'Campaign cloned',
        description: `"${cloneName}" was created successfully.`,
      });
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Failed to clone campaign',
        description: error instanceof Error ? error.message : 'Please try again.',
      });
    } finally {
      setDuplicatingCampaignId(null);
    }
  };

  const handleCopyId = async (campaign: Campaign) => {
    try {
      await navigator.clipboard.writeText(campaign.id);
      toast({
        variant: 'success',
        title: 'Campaign ID copied',
      });
    } catch {
      toast({
        variant: 'destructive',
        title: 'Could not copy campaign ID',
      });
    }
  };

  const handleRowNavigate = (campaign: Campaign) => {
    if (canEditCampaign(campaign.status)) {
      navigate(`/campaigns/edit/${campaign.id}`);
      return;
    }
    if (campaign.displayStatus === 'Sent') {
      navigate(`/reports/campaigns/${campaign.id}`);
    }
  };

  const handleConfirmUnschedule = async () => {
    if (!unscheduleTarget) return;
    try {
      const result = await cancelSchedule(unscheduleTarget.id);
      toast({
        variant: 'success',
        title:
          result.campaignStatus === 'draft'
            ? 'Campaign moved back to draft'
            : 'Schedule cancelled',
        description:
          result.campaignStatus === 'draft'
            ? undefined
            : `Campaign status is now ${result.campaignStatus}.`,
      });
      setUnscheduleTarget(null);
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Could not unschedule that campaign',
        description: error instanceof Error ? error.message : 'Try again.',
      });
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget || isDeleting) return;
    try {
      await deleteCampaign(deleteTarget.id);
      toast({
        variant: 'success',
        title: 'Campaign deleted',
        description: `"${deleteTarget.name}" was deleted.`,
      });
      setDeleteTarget(null);
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Failed to delete campaign',
        description: error instanceof Error ? error.message : 'Please try again.',
      });
    }
  };

  return (
    <TooltipProvider delayDuration={200}>
      <Table className="min-w-[1080px]" divClassname="min-w-0 overflow-x-auto">
        <TableHeader>
          <TableRow className="hover:bg-transparent">
            <TableHead className="sticky left-0 top-0 z-30 min-w-[14rem] border-r border-border bg-neutral-50 px-4 text-xs font-semibold text-muted-foreground">
              Name
            </TableHead>
            <TableHead className="sticky top-0 z-10 min-w-[7rem] bg-muted/40 px-4 text-xs font-semibold text-muted-foreground">
              Channel
            </TableHead>
            <TableHead className="sticky top-0 z-10 min-w-[7rem] bg-muted/40 px-4 text-xs font-semibold text-muted-foreground">
              Status
            </TableHead>
            <TableHead className="sticky top-0 z-10 min-w-[7rem] bg-muted/40 px-4 text-right text-xs font-semibold text-muted-foreground">
              Audience
            </TableHead>
            <TableHead className="sticky top-0 z-10 min-w-[7rem] bg-muted/40 px-4 text-right text-xs font-semibold text-muted-foreground">
              Sendable
            </TableHead>
            <TableHead className="sticky top-0 z-10 min-w-[6rem] bg-muted/40 px-4 text-right text-xs font-semibold text-muted-foreground">
              Open rate
            </TableHead>
            <TableHead className="sticky top-0 z-10 min-w-[6rem] bg-muted/40 px-4 text-right text-xs font-semibold text-muted-foreground">
              Click rate
            </TableHead>
            <TableHead className="sticky top-0 z-10 min-w-[11rem] bg-muted/40 px-4 text-xs font-semibold text-muted-foreground">
              Scheduled / sent
            </TableHead>
            <TableHead className="sticky top-0 z-10 w-12 bg-muted/40 px-4" />
          </TableRow>
        </TableHeader>
        <TableBody className="[&_tr:last-child]:!border-b">
          {isLoading ? <CampaignsTableSkeletonRows /> : null}

          {!isLoading && isError ? (
            <TableRow className="hover:bg-transparent">
              <TableCell colSpan={9} className="px-6 py-14">
                <div className="mx-auto flex max-w-[360px] flex-col items-center gap-3 text-center">
                  <WarningCircle className="size-8 text-[#D80032]" aria-hidden />
                  <p className="text-base font-semibold text-foreground">
                    Couldn&apos;t load campaigns
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {errorMessage ||
                      'Something went wrong while fetching your campaigns. Check your connection and try again.'}
                  </p>
                  {onRetry ? (
                    <Button
                      type="button"
                      className="mt-1 gap-2 bg-primary-600 hover:bg-primary-700"
                      onClick={onRetry}
                    >
                      <ArrowClockwise className="size-4" aria-hidden />
                      Retry
                    </Button>
                  ) : null}
                </div>
              </TableCell>
            </TableRow>
          ) : null}

          {!isLoading && !isError && isEmpty ? (
            <TableRow className="hover:bg-transparent">
              <TableCell colSpan={9} className="px-6 py-14">
                <div className="mx-auto flex max-w-[360px] flex-col items-center gap-3 text-center">
                  {hasActiveFilters ? (
                    <MagnifyingGlass className="size-8 text-[#707070]" aria-hidden />
                  ) : (
                    <Megaphone className="size-8 text-[#707070]" aria-hidden />
                  )}
                  <p className="text-base font-semibold text-foreground">
                    {hasActiveFilters ? 'No campaigns match' : 'No campaigns yet'}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {hasActiveFilters
                      ? 'Try a different search term, or clear your status and channel filters to see everyone.'
                      : 'Create your first campaign to reach your audience by email.'}
                  </p>
                  <Button
                    type="button"
                    className="mt-1 gap-2 bg-primary-600 hover:bg-primary-700"
                    onClick={hasActiveFilters ? onClearFilters : onCreate}
                  >
                    {hasActiveFilters ? (
                      <XCircle className="size-4" aria-hidden />
                    ) : (
                      <Plus className="size-4" aria-hidden />
                    )}
                    {hasActiveFilters ? 'Clear filters' : 'New campaign'}
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          ) : null}

          {!isLoading &&
            !isError &&
            campaigns.map((campaign) => {
              const editPath = `/campaigns/edit/${campaign.id}`;
              const isDraft = canEditCampaign(campaign.status);
              const isScheduled = campaign.displayStatus === 'Scheduled';
              const isSent = campaign.displayStatus === 'Sent';
              const isSending = campaign.displayStatus === 'Sending';
              const isRowDuplicating = duplicatingCampaignId === campaign.id;

              return (
                <TableRow
                  key={campaign.id}
                  className="cursor-pointer hover:bg-muted/40"
                  onClick={() => handleRowNavigate(campaign)}
                >
                  <TableCell className="sticky left-0 z-10 border-r border-border bg-background px-4 py-3 align-middle shadow-[inset_0_-1px_0_0_#E7E8EA]">
                    <div className="min-w-0">
                      <span className="block truncate text-sm font-medium text-foreground">
                        {campaign.name}
                      </span>
                      {campaign.subject ? (
                        <p className="max-w-[260px] truncate text-xs text-muted-foreground">
                          {campaign.subject}
                        </p>
                      ) : null}
                    </div>
                  </TableCell>
                  <TableCell className="px-4 py-3 align-middle text-sm text-muted-foreground">
                    {campaign.channel}
                  </TableCell>
                  <TableCell className="px-4 py-3 align-middle">
                    <CampaignStatusBadge status={campaign.displayStatus} />
                  </TableCell>
                  <TableCell className="px-4 py-3 text-right align-middle text-sm tabular-nums text-foreground">
                    {formatCount(campaign.audienceSize)}
                  </TableCell>
                  <TableCell className="px-4 py-3 text-right align-middle text-sm tabular-nums text-muted-foreground">
                    {formatCount(campaign.sendableCount)}
                  </TableCell>
                  <TableCell className="px-4 py-3 text-right align-middle text-sm tabular-nums text-muted-foreground">
                    {formatRate(campaign.openRate)}
                  </TableCell>
                  <TableCell className="px-4 py-3 text-right align-middle text-sm tabular-nums text-muted-foreground">
                    {formatRate(campaign.clickRate)}
                  </TableCell>
                  <TableCell className="px-4 py-3 align-middle text-sm whitespace-nowrap text-muted-foreground">
                    {campaign.scheduledOrSentAt ?? '—'}
                  </TableCell>
                  <TableCell
                    className="px-4 py-3 text-center align-middle"
                    onClick={(event) => event.stopPropagation()}
                  >
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          type="button"
                          size="icon"
                          variant="ghost"
                          className="h-8 w-8 text-muted-foreground hover:text-foreground"
                          aria-label={`Actions for ${campaign.name}`}
                          disabled={isRowDuplicating}
                        >
                          <DotsThreeVertical className="size-[18px]" weight="bold" aria-hidden />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-52">
                        <DropdownMenuItem
                          disabled={isDuplicating}
                          onSelect={(event) => {
                            event.preventDefault();
                            void handleDuplicateCampaign(campaign);
                          }}
                        >
                          <Copy className="mr-2 h-4 w-4" aria-hidden />
                          {isRowDuplicating ? 'Duplicating…' : 'Duplicate'}
                        </DropdownMenuItem>
                        <DropdownMenuItem onSelect={() => void handleCopyId(campaign)}>
                          <Hash className="mr-2 h-4 w-4" aria-hidden />
                          Copy ID
                        </DropdownMenuItem>
                        {isDraft ? (
                          <DropdownMenuItem onSelect={() => navigate(editPath)}>
                            <PencilSimple className="mr-2 h-4 w-4" aria-hidden />
                            Edit
                          </DropdownMenuItem>
                        ) : (
                          <DisabledMenuItem
                            icon={<PencilSimple className="mr-2 h-4 w-4" aria-hidden />}
                            label="Edit"
                            tooltip="Only draft campaigns can be edited."
                          />
                        )}
                        {isDraft ? (
                          <DropdownMenuItem
                            onSelect={() =>
                              navigate(editPath, {
                                state: {
                                  wizardStep: 3,
                                  wizardStepVersion: WIZARD_STEP_VERSION,
                                },
                              })
                            }
                          >
                            <Article className="mr-2 h-4 w-4" aria-hidden />
                            Edit content
                          </DropdownMenuItem>
                        ) : (
                          <DisabledMenuItem
                            icon={<Article className="mr-2 h-4 w-4" aria-hidden />}
                            label="Edit content"
                            tooltip="Content is editable only while a campaign is a draft."
                          />
                        )}
                        {isScheduled ? (
                          <DropdownMenuItem onSelect={() => setUnscheduleTarget(campaign)}>
                            <CalendarX className="mr-2 h-4 w-4" aria-hidden />
                            Unschedule
                          </DropdownMenuItem>
                        ) : (
                          <DisabledMenuItem
                            icon={<CalendarX className="mr-2 h-4 w-4" aria-hidden />}
                            label="Unschedule"
                            tooltip="Only a scheduled campaign can be unscheduled."
                          />
                        )}
                        {isSent ? (
                          <DropdownMenuItem
                            onSelect={() => navigate(`/reports/campaigns/${campaign.id}`)}
                          >
                            <ChartLine className="mr-2 h-4 w-4" aria-hidden />
                            View report
                          </DropdownMenuItem>
                        ) : (
                          <DisabledMenuItem
                            icon={<ChartLine className="mr-2 h-4 w-4" aria-hidden />}
                            label="View report"
                            tooltip="Available once this campaign has sent."
                          />
                        )}
                        <DropdownMenuSeparator />
                        {isSending ? (
                          <DisabledMenuItem
                            icon={<Trash className="mr-2 h-4 w-4" aria-hidden />}
                            label="Delete"
                            tooltip="Wait for the send to finish before deleting."
                          />
                        ) : (
                          <DropdownMenuItem
                            className="text-destructive focus:text-destructive"
                            onSelect={() => setDeleteTarget(campaign)}
                          >
                            <Trash className="mr-2 h-4 w-4" aria-hidden />
                            Delete
                          </DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              );
            })}
        </TableBody>
      </Table>

      <ConfirmationModal
        open={Boolean(unscheduleTarget)}
        onOpenChange={(open) => {
          if (!open && !isCancelling) setUnscheduleTarget(null);
        }}
        title="Unschedule campaign?"
        description={
          unscheduleTarget
            ? `“${unscheduleTarget.name}” will move back to draft. Its scheduled time is cleared; audience and content stay as they are.`
            : ''
        }
        confirmText={isCancelling ? 'Unscheduling…' : 'Unschedule'}
        cancelText="Cancel"
        preventAutoClose
        onConfirm={() => void handleConfirmUnschedule()}
      />

      <ConfirmationModal
        open={Boolean(deleteTarget)}
        onOpenChange={(open) => {
          if (!open) setDeleteTarget(null);
        }}
        title="Delete campaign?"
        description={
          deleteTarget
            ? `“${deleteTarget.name}” will be permanently removed. This can’t be undone.`
            : ''
        }
        confirmText={isDeleting ? 'Deleting…' : 'Delete'}
        cancelText="Cancel"
        confirmClassName="bg-destructive text-destructive-foreground hover:bg-destructive/90"
        preventAutoClose
        onConfirm={() => void handleConfirmDelete()}
      />
    </TooltipProvider>
  );
};
